/** Public cover page at the main address (visitors who aren't signed in). */
import { useEffect } from 'react';
import { useRouter } from './router.tsx';
import { PalmettoMark } from './login.tsx';

const HOUND_FEATURES = [
  { t: 'Bulk AI scanning', d: 'Scan a whole stack of court papers at once. CivilHound splits them into individual papers, reads the case details, and flags anything it isn’t sure about. No more keying every paper in by hand.' },
  { t: 'Routes built on service history', d: 'Routes are planned around the best time to catch each defendant, based on when past attempts at that person and address actually succeeded.' },
  { t: 'Suggested shift times', d: 'CivilHound studies prior service trends and suggests when servers should be working to give every paper its best chance of being served.' },
  { t: 'Customer portal with live alerts', d: 'Customers check the status of their papers online instead of calling the office, and get real-time alerts the moment a paper is attempted or served.' },
  { t: 'Law office portal and card payments', d: 'Law offices upload papers directly and pay by card. Papers land in the queue ready for review, with no mail, no counter visit, and no retyping.' },
  { t: 'IV-D reporting', d: 'Child support (Title IV-D) service is tracked as it happens, so the reports are ready when they’re due instead of being rebuilt by hand.' },
  { t: 'Executions handled', d: 'Executions, evictions and set-outs are managed alongside every other paper, with the timing rules, scheduling and documentation in one place.' },
  { t: 'South Carolina rules built in', d: 'Attempt counts, time windows, Sundays and holidays, and eviction timing are checked automatically before every attempt.' },
  { t: 'Safety alerts that travel', d: 'Flag an aggressive dog, a weapon or a violent history once, and every future server sees it before they knock.' },
];

const LOGIX_ATTORNEYS = [
  { t: 'Send papers without the trip', d: 'Send papers to your private server, or straight to any sheriff’s office running CivilHound. No mail, no counter visit, no retyping.' },
  { t: 'Live status on every paper', d: 'See each attempt as it happens, with the date, time and result, across every server and every county you use.' },
  { t: 'Affidavits back, filed to the matter', d: 'Signed affidavits of service come back as PDFs, attached to the right case, ready to file.' },
  { t: 'Pay by card, one view of costs', d: 'Pay service fees online and see what you’ve spent by client, by matter and by county.' },
];

const LOGIX_SERVERS = [
  { t: 'One inbox for every client', d: 'Jobs from law firms arrive with the case details already filled in. Accept them, route them, serve them.' },
  { t: 'Routes that learn', d: 'The same service-history engine as CivilHound: every attempt teaches the system when each person is most likely to be home.' },
  { t: 'Proof that holds up', d: 'Every attempt is GPS- and time-stamped from your phone, with photos and notes, so your affidavit is backed by the record.' },
  { t: 'Affidavits and invoices done for you', d: 'Affidavits are drafted from your attempts and signed on the phone. Invoices go out when the job is done, and clients pay online.' },
];

const DEMO_MAIL = 'mailto:info@civilprocesssoftware.com?subject=CivilHound%20demo%20request';
const WAITLIST_MAIL = 'mailto:info@civilprocesssoftware.com?subject=CivilLogix%20waitlist';

export function Landing() {
  const { navigate } = useRouter();
  const signIn = () => navigate('/signin');
  useEffect(() => {
    const prev = document.title;
    document.title = 'CivilHound & CivilLogix · Civil Process Software';
    return () => {
      document.title = prev;
    };
  }, []);
  return (
    <div className="landing">
      <header className="landing-top">
        <div className="landing-brand">
          <span>Civil Process Software</span>
          <span className="landing-brand-by">by 803 Systems, LLC</span>
        </div>
        <nav className="landing-nav" aria-label="Products">
          <a href="#civilhound">CivilHound</a>
          <a href="#civillogix">CivilLogix</a>
          <a href="#about">About</a>
        </nav>
        <button className="btn landing-signin" onClick={signIn}>
          Sign in
        </button>
      </header>

      <section className="landing-hero lp-hero2">
        <div className="lp-hero-copy">
          <p className="lp-built">Built by a South Carolina deputy with nearly 20 years in civil process</p>
          <h1>Civil process software that deputies want to use.</h1>
          <p className="landing-lead">
            CivilHound runs a sheriff’s civil division, from the stack on the clerk’s desk to the deputy at the door to the signed affidavit going back to court. Every attempt is GPS-stamped, South Carolina’s rules are checked before every knock, and it keeps working when the car has no signal.
          </p>
          <div className="landing-cta">
            <a className="btn primary" href={DEMO_MAIL}>
              Request a demo
            </a>
            <a className="btn landing-ghost" href="#inside">
              See it working
            </a>
          </div>
        </div>
        <div className="lp-hero-shot" aria-hidden="true">
          <div className="lp-browser">
            <div className="lp-browser-bar">
              <i />
              <i />
              <i />
            </div>
            <img src="/mk-papers.webp" alt="" width={1600} height={1000} />
          </div>
          <img className="lp-phone" src="/mk-phone.webp" alt="" width={300} height={649} />
        </div>
      </section>

      <section className="landing-section lp-two">
        <h2 className="lp-two-h">Two products, one system</h2>
        <div className="lp-products">
          <article className="lp-product hound">
            <img src="/civilhound-badge.webp" alt="CivilHound logo: a bloodhound carrying a paper, framed by a six-point sheriff’s star" width={220} height={220} />
            <div className="lp-product-body">
              <span className="lp-for">For sheriffs’ offices</span>
              <h2>CivilHound</h2>
              <p>The complete civil process system for sheriffs’ civil divisions, from the stack on the clerk’s desk to the deputy at the door to the signed affidavit going back to court.</p>
              <span className="lp-chip">Demos early 2027</span>
              <div className="lp-actions">
                <a className="btn primary" href="#civilhound">
                  See CivilHound
                </a>
                <a className="btn" href={DEMO_MAIL}>
                  Request a demo
                </a>
              </div>
            </div>
          </article>
          <article className="lp-product logix">
            <img src="/civillogix.webp" alt="CivilLogix logo: a laptop showing a paper list and a route map" width={220} height={220} />
            <div className="lp-product-body">
              <span className="lp-for">For attorneys &amp; private process servers</span>
              <h2>CivilLogix</h2>
              <p>Send papers, track every attempt and get affidavits back, whether a private server or a sheriff’s office is doing the serving.</p>
              <span className="lp-chip muted">In development</span>
              <div className="lp-actions">
                <a className="btn primary" href="#civillogix">
                  See CivilLogix
                </a>
                <a className="btn" href={WAITLIST_MAIL}>
                  Join the waitlist
                </a>
              </div>
            </div>
          </article>
        </div>
      </section>

      {/* ─── Inside CivilHound: real screens from the showroom agency ─── */}
      <section className="landing-section lp-inside" id="inside">
        <h2>See it working</h2>
        <p className="lp-inside-lead">Screens from CivilHound’s demonstration agency. Every name, address and case in them is made up.</p>
        <div className="lp-shots">
          <figure className="lp-shot wide">
            <img src="/mk-paper.webp" alt="A paper's record: number, defendant, case, court date, customer, deputy and fee across the top, with service details, rules and the affidavit below" width={1600} height={1000} loading="lazy" />
            <figcaption>
              <strong>Every paper’s whole story in one place.</strong> Who it’s for, where it stands, every attempt with its time and GPS, the rules that apply, and the affidavit, from intake to the court.
            </figcaption>
          </figure>
          <figure className="lp-shot">
            <img src="/mk-dashboard.webp" alt="Needs attention: papers by stage from review to completed, and the problems someone has to act on" width={1600} height={1000} loading="lazy" />
            <figcaption>
              <strong>The whole civil division on one screen.</strong> Papers by stage, from intake to filed with the court, and the exceptions that need a person, grouped so nothing gets buried.
            </figcaption>
          </figure>
          <figure className="lp-shot">
            <img src="/mk-writbook.webp" alt="The Writ Book: every civil paper received, who served it, when and how, and the sheriff's costs" width={1600} height={1000} loading="lazy" />
            <figcaption>
              <strong>The Writ Book keeps itself.</strong> Every paper received, who served it, when and how, and the sheriff’s costs, ready to print or export, with South Carolina’s retention schedule built in.
            </figcaption>
          </figure>
        </div>
      </section>

      {/* ─── CivilHound ─────────────────────────────────────────────── */}
      <section className="landing-section lp-prodhead" id="civilhound">
        <div className="lp-prodhead-grid">
          <img src="/civilhound-trail.webp" alt="The CivilHound bloodhound following a route to a house, carrying papers" width={360} height={360} />
          <div>
            <span className="landing-kicker">CivilHound · for sheriffs’ offices</span>
            <h2>Serving civil papers is careful, detailed work.</h2>
            <p>
              Every summons, eviction and subpoena has its own rules about when, where and how it can be served. The paperwork has to hold up in court, and the deputy doing it is often alone at a stranger’s door.
            </p>
            <p>
              Too often that work runs on hand-keyed entries, paper logs and one server’s memory, with software that costs too much or was never designed for it. CivilHound is built from the ground up for the way civil process actually works. It checks the rules for you, keeps the record for you, keeps the public informed, and looks after the people in the field.
            </p>
          </div>
        </div>
        <div className="landing-grid">
          {HOUND_FEATURES.map((f) => (
            <div key={f.t} className="landing-card">
              <h3>{f.t}</h3>
              <p>{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="landing-band">
        <div className="landing-split">
          <div>
            <span className="landing-kicker">Service history</span>
            <h2>What your best server knows, kept for good.</h2>
            <p>
              Ask a veteran server when to catch John Doe at home and they’ll tell you: “Tuesday evenings, after six. Never Saturday mornings.” That knowledge is earned one knock at a time, and until now it lived only in their head. When they retired, transferred or moved to another position, it was gone.
            </p>
            <p>
              CivilHound keeps it. Every attempt is recorded with the day, time, result and location, so the office builds a history of when each person and address is most likely to answer the door. That history shapes routes and suggested shift times, so the next server starts where the last one left off, with fewer wasted trips and fewer unsuccessful attempts for every paper served.
            </p>
          </div>
          <ul className="landing-points">
            <li>
              <strong>Every attempt counts.</strong> Day, time, outcome and GPS are captured automatically, not in a notebook.
            </li>
            <li>
              <strong>Patterns per person and address.</strong> Past service to the same defendant or house tells you when to go back.
            </li>
            <li>
              <strong>Smarter routes and shifts.</strong> Stops and shift times are suggested for the best chance of service.
            </li>
            <li>
              <strong>Knowledge that stays.</strong> When a server moves on, what they learned stays with the office.
            </li>
          </ul>
        </div>
      </section>

      <section className="landing-section">
        <div className="landing-split reverse">
          <SatelliteIllustration />
          <div>
            <span className="landing-kicker">Satellite mapping</span>
            <h2>Find the house, not just the address.</h2>
            <p>
              Not every house has a number on the mailbox. Long shared driveways, homes set back in the woods, mobile home parks and new construction can send a server to the wrong door, or back to the office with nothing.
            </p>
            <p>
              CivilHound lets you switch to satellite view and drop a pin on the exact house. That pin is saved with the address, used for turn-by-turn navigation and GPS verification, and waiting for the next server, the next paper and the next time.
            </p>
          </div>
        </div>
      </section>

      <section className="landing-band landing-safety">
        <div className="landing-split">
          <div>
            <span className="landing-kicker">Officer safety</span>
            <h2>Know before you knock.</h2>
            <p>
              The server who warned everyone about the dog at the end of that dirt road shouldn’t have to be there for the warning to reach the next deputy. In CivilHound, servers flag an address or a person with a safety alert from a set list chosen by the agency: aggressive dog, armed, violent history, mental health concern, do not serve alone.
            </p>
            <p>
              The alert pops up the moment a server opens that stop and has to be acknowledged before they go on. Address alerts stay with the house for future papers. Person alerts follow the defendant to any address. Only a supervisor can remove one.
            </p>
            <p>
              And if a server goes quiet at a door too long, their phone asks “Are you OK?”. If there’s no answer, dispatch is alerted with their last location.
            </p>
          </div>
          <div className="landing-alerts" aria-label="Example safety alerts">
            <div className="alert-card address">
              <span className="alert-tag">Address alert</span>
              <strong>🐕 Aggressive dog</strong>
              <span>Large dog loose in the yard. Announce from the vehicle first.</span>
            </div>
            <div className="alert-card person">
              <span className="alert-tag">Person alert</span>
              <strong>⚠️ Violent history</strong>
              <span>Follows the defendant to every address. Backup recommended.</span>
            </div>
            <div className="alert-card address">
              <span className="alert-tag">Address alert</span>
              <strong>👥 Do not serve alone</strong>
              <span>Two servers required. Contact the supervisor before approaching.</span>
            </div>
            <div className="alert-ack">✓ Acknowledged by the server before proceeding</div>
          </div>
        </div>
      </section>

      {/* ─── CivilLogix ─────────────────────────────────────────────── */}
      <section className="landing-section lp-prodhead lp-logix" id="civillogix">
        <div className="lp-prodhead-grid">
          <img src="/civillogix.webp" alt="CivilLogix logo" width={320} height={320} />
          <div>
            <span className="landing-kicker">CivilLogix · for attorneys &amp; private process servers · in development</span>
            <h2>The other side of the paper.</h2>
            <p>
              Every paper a sheriff’s office serves started on someone’s desk: a law office filing an eviction, a collection, a divorce. Plenty more are served by private process servers. Both have the same questions every day: <em>Has it been served yet? When did they try? Where’s my affidavit?</em>
            </p>
            <p>CivilLogix is built on the same foundation as CivilHound, for the attorneys who send papers and the private servers who serve them.</p>
          </div>
        </div>
        <div className="lp-audiences">
          <div>
            <h3 className="lp-aud-title">For law offices</h3>
            <div className="lp-aud-grid">
              {LOGIX_ATTORNEYS.map((f) => (
                <div key={f.t} className="landing-card">
                  <h3>{f.t}</h3>
                  <p>{f.d}</p>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h3 className="lp-aud-title">For private process servers</h3>
            <div className="lp-aud-grid">
              {LOGIX_SERVERS.map((f) => (
                <div key={f.t} className="landing-card">
                  <h3>{f.t}</h3>
                  <p>{f.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="landing-band lp-together">
        <span className="landing-kicker">Better together</span>
        <h2>From the law office to the front door and back, with no retyping.</h2>
        <ol className="lp-flow">
          <li>
            <span className="lp-flow-n">1</span>
            <strong>A law office sends a paper in CivilLogix</strong>
            <span>Case details, defendant and address are entered once, by the people who know them.</span>
          </li>
          <li>
            <span className="lp-flow-n">2</span>
            <strong>It lands at the sheriff’s office in CivilHound</strong>
            <span>Already keyed and ready for review: no mail, no counter visit, no hand entry.</span>
          </li>
          <li>
            <span className="lp-flow-n">3</span>
            <strong>Status and affidavits flow back</strong>
            <span>Every attempt shows up for the attorney as it happens, and the signed affidavit comes back to the case.</span>
          </li>
        </ol>
      </section>

      <section className="landing-section landing-about" id="about">
        <div className="landing-about-grid">
          <div>
            <h2>About the founder</h2>
            <p>
              <strong>David Peeples</strong> has been a deputy sheriff with Chester County, South Carolina for nearly 20 years, and has spent most of his career in civil process, from serving papers on the street to managing the unit.
            </p>
            <p>
              He saw two problems up close. The first was time: hours spent keying papers in by hand. The second was knowledge. Civil process units serve many of the same people again and again, and an experienced server learns the best time to catch someone at home. But that knowledge walks out the door when the server retires, transfers or changes positions.
            </p>
            <p>
              After searching for civil process software that was affordable, efficient and able to solve both, he decided to build his own. CivilHound is the system he wished his unit had. CivilLogix brings the same approach to the attorneys and private servers on the other end of every paper.
            </p>
          </div>
          <div>
            <h2>About 803 Systems</h2>
            <p>
              CivilHound and CivilLogix are developed by <strong>803 Systems, LLC</strong>, a South Carolina company based in Chester. The name is home: 803 is the area code for Chester and the Midlands. Small-town roots, a plain-spoken way of doing business, and a commitment to building things that last.
            </p>
            <div className="landing-home">
              <PalmettoMark />
              <span>Proudly built in Chester, South Carolina</span>
            </div>
          </div>
        </div>
      </section>

      <section className="landing-section landing-final">
        <h2>Get in touch</h2>
        <div className="lp-final-grid">
          <div className="lp-final-card">
            <img src="/civilhound-icon.webp" alt="" width={72} height={72} />
            <h3>Sheriffs’ offices</h3>
            <p>CivilHound demonstrations for sheriffs’ offices will be available in early 2027.</p>
            <a className="btn primary big" href={DEMO_MAIL}>
              Request a CivilHound demo
            </a>
          </div>
          <div className="lp-final-card">
            <img src="/civillogix.webp" alt="" width={72} height={72} />
            <h3>Attorneys &amp; private process servers</h3>
            <p>CivilLogix is in development. Join the waitlist to hear first when it opens.</p>
            <a className="btn primary big" href={WAITLIST_MAIL}>
              Join the CivilLogix waitlist
            </a>
          </div>
        </div>
        <p className="landing-contact">
          Questions? Email <a href="mailto:info@civilprocesssoftware.com">info@civilprocesssoftware.com</a>
        </p>
        <div className="landing-cta landing-cta-center">
          <button className="btn big" onClick={signIn}>
            Staff sign in
          </button>
        </div>
      </section>

      <footer className="landing-footer">
        <span>© {new Date().getFullYear()} 803 Systems, LLC · Chester, South Carolina · CivilHound™ and CivilLogix™</span>
        <a href="mailto:info@civilprocesssoftware.com">info@civilprocesssoftware.com</a>
      </footer>
    </div>
  );
}

/** Stylised aerial view: a rural road, an unnumbered shared driveway, and a corrected pin on the right house. */
function SatelliteIllustration() {
  return (
    <figure className="landing-sat">
      <svg viewBox="0 0 600 420" role="img" aria-label="Aerial view of a rural road. The geocoded address point sits on the road, while a corrected pin marks the house at the end of a long shared driveway.">
        <defs>
          <pattern id="field" width="12" height="12" patternUnits="userSpaceOnUse">
            <rect width="12" height="12" fill="#5d7b47" />
            <path d="M0 12L12 0" stroke="#66864e" strokeWidth="3" />
          </pattern>
          <radialGradient id="tree" cx="40%" cy="35%" r="70%">
            <stop offset="0%" stopColor="#3f6b35" />
            <stop offset="100%" stopColor="#223f1f" />
          </radialGradient>
        </defs>
        <rect width="600" height="420" fill="url(#field)" />
        <path d="M0 250 Q 150 280 300 240 T 600 220 L600 420 L0 420Z" fill="#6f8a52" opacity="0.55" />
        {/* road */}
        <path d="M-10 330 C 140 312, 300 350, 610 300" stroke="#4b4b48" strokeWidth="34" fill="none" />
        <path d="M-10 330 C 140 312, 300 350, 610 300" stroke="#d8c46a" strokeWidth="2" strokeDasharray="14 12" fill="none" />
        {/* shared driveway */}
        <path d="M250 334 C 262 280, 300 240, 330 200 S 390 120, 430 104" stroke="#b9a27a" strokeWidth="9" fill="none" strokeLinecap="round" />
        <path d="M300 238 C 250 225, 215 205, 190 176" stroke="#b9a27a" strokeWidth="8" fill="none" strokeLinecap="round" />
        {/* trees */}
        {[
          [60, 80, 34], [110, 60, 26], [150, 110, 30], [480, 200, 36], [540, 160, 30], [520, 250, 24], [360, 60, 28], [300, 110, 22],
          [410, 170, 22], [70, 190, 28], [250, 150, 20], [560, 80, 30], [460, 40, 24],
        ].map(([x, y, r], i) => (
          <circle key={i} cx={x} cy={y} r={r} fill="url(#tree)" />
        ))}
        {/* houses: roofs with ridge lines */}
        <g>
          <rect x="160" y="150" width="46" height="34" rx="2" fill="#8d8f93" transform="rotate(-18 183 167)" />
          <line x1="160" y1="167" x2="206" y2="167" stroke="#6d6f73" strokeWidth="2" transform="rotate(-18 183 167)" />
          <rect x="420" y="84" width="54" height="38" rx="2" fill="#9a7b64" transform="rotate(-24 447 103)" />
          <line x1="420" y1="103" x2="474" y2="103" stroke="#7c604c" strokeWidth="2" transform="rotate(-24 447 103)" />
          <rect x="70" y="270" width="40" height="30" rx="2" fill="#a3a5a8" />
          <line x1="70" y1="285" x2="110" y2="285" stroke="#838588" strokeWidth="2" />
          <rect x="470" y="330" width="44" height="30" rx="2" fill="#8f9194" />
          <line x1="470" y1="345" x2="514" y2="345" stroke="#707275" strokeWidth="2" />
        </g>
        {/* mailbox at the road, no number */}
        <rect x="236" y="300" width="10" height="8" fill="#e6e6e6" stroke="#333" strokeWidth="1" />
        {/* geocoded address point (on the road) */}
        <circle cx="252" cy="330" r="11" fill="none" stroke="#fff" strokeWidth="3" strokeDasharray="4 3" />
        <circle cx="252" cy="330" r="3" fill="#fff" />
        {/* connector */}
        <path d="M258 322 C 300 250, 380 170, 440 118" stroke="#fca311" strokeWidth="2.5" strokeDasharray="6 5" fill="none" />
        {/* corrected pin on the back house */}
        <g transform="translate(447 44)">
          <path d="M0 58C0 58 22 32 22 18A22 22 0 0 0-22 18C-22 32 0 58 0 58Z" fill="#fca311" stroke="#14213d" strokeWidth="3" />
          <path d="M-9 20L0 11L9 20V29H-9Z" fill="#14213d" />
        </g>
      </svg>
      <figcaption>
        <span>
          <i className="dot-legend geo" /> Where the address lands on the road
        </span>
        <span>
          <i className="dot-legend pin" /> The pin a server set on the right house
        </span>
      </figcaption>
    </figure>
  );
}
